<template>
  <div v-if="ready" class="content-with-spacing">
    <div class="panel panel-body employer-merging">
      <h1>Employer Merging</h1>
      <p class="info-text">
        Start by selecting a <b>Source Employer</b>, and then choose a <b>Destination Employer</b>. Once you proceed, a
        confirmation table will appear. This table will display both the selected source and destination employers,
        along with any employers that have been previously merged into them. <i><b>Please review this carefully to ensure </b></i>
        <i><b>they are correct.</b></i> Completing the merge will transfer all learners from the source to the destination employer.
      </p>
      <div class="employer-merging-form">
        <div class="form-group">
          <label for="source-employer">
            Source Employer
            <span class="form-required">*</span>
          </label>
          <k-dropdown
            id="source-employer"
            class="dropdown"
            :options="employerOptions"
            :showSearch="true"
            v-model="sourceEmployerId"
            placeholder="Select source employer"
          />
        </div>
        <div class="form-group">
          <label for="destination-employer">
            Destination Employer
            <span class="form-required">*</span>
          </label>
          <k-dropdown
            id="destination-employer"
            class="dropdown"
            :options="destinationEmployerOptions"
            :showSearch="true"
            v-model="destinationEmployerId"
            :disabled="!sourceEmployerId || !destinationEmployerOptions.length"
            placeholder="Select destination employer"
          />
        </div>
      </div>
      <div v-if="!hideButtons" class="employer-merging-actions">
        <button class="btn btn-secondary" @click="clearEmployers">Clear</button>
        <button class="btn btn-primary" @click="prepareMerge" :disabled="!sourceEmployerId || !destinationEmployerId">Merge Employers</button>
      </div>
      <div v-if="employersToDisplay.length">
        <h2>Confirm Merge</h2>
        <p>Please review the source and destination employers before finalizing the merge.</p>
        <k-table
          :headers="kableHeaders"
          :rows="kableRows"
          :max="10"
          :hideable="false"
          :panel="false"
          :enableTopScroll="true"
          :empty-message="'No employers to display'"
        />
        <div class="employer-merging-actions confirmation-actions">
          <button class="btn btn-danger" @click="clearEmployers"><i class="fas fa-times" aria-hidden="true"></i> Cancel</button>
          <button class="btn btn-success" @click="confirmMergeEmployers"><i class="fas fa-check" aria-hidden="true"></i> Finish</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.employer-merging {
  box-shadow: var(--box-shadow);
}

.employer-merging-form {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-top: 20px;
}

.form-group {
  flex: 1 1 260px;
  min-width: 260px;
}

.dropdown {
  width: 100%;
}

.help-text {
  margin-top: 8px;
  font-size: 0.9em;
  color: var(--kate-type-secondary);
}

.employer-merging-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.employer-merging-table {
  margin-top: 30px;
}
</style>

<script>
import KDropdown from '@base-components/k-dropdown.vue';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KTable from '@base-components/tables/display/k-table.vue';

export default {
  components: {
    KDropdown,
    KTable,
  },

  mixins: [PageReadyMixin],

  beforeMount() {
    this.$Loading.start();
    this.getEmployers();
  },

  watch: {
    sourceEmployerId() {
      this.destinationEmployerId = undefined;
    },
  },

  data() {
    return {
      employers: [],
      employersReady: false,
      sourceEmployerId: undefined,
      destinationEmployerId: undefined,
      employersToDisplay: [],
      hideButtons: false,
      kableHeaders: {
        role: {
          name: 'Role',
        },
        name: {
          name: 'Name',
        },
        merged_employers: {
          name: 'Merged Employers',
          type: 'tags',
        },
      },
    };
  },

  computed: {
    ready() {
      return this.employersReady;
    },

    kableRows() {
      return this.employersToDisplay.map(e => ({
        name: e.name,
        role: e.role,
        merged_employers: {
          tags: e.merged_employers.map(me => me.pics_employer_name),
        },
      }));
    },

    employerOptions() {
      return this.employers.map(e => ({
        id: e.id,
        name: e.name,
      }));
    },

    sourceEmployer() {
      return this.employers.find(e => e.id === this.sourceEmployerId);
    },

    destinationEmployer() {
      return this.employers.find(e => e.id === this.destinationEmployerId);
    },

    destinationEmployerOptions() {
      if (!this.sourceEmployer) {
        return [];
      }
      return this.employers
        .filter(e => e.id !== this.sourceEmployer.id)
        .map(e => ({
          id: e.id,
          name: e.name,
        }));
    },
  },

  methods: {
    getEmployers() {
      this.employersReady = false;
      this.$logger.info('Getting employers for employer merging');
      this.$http.get('/api/profile/employers').then(res => {
        this.$logger.info('Successfully retrieved employers for employer merging');
        this.employers = res.data;
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to retrieve employers for employer merging', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.employersReady = true;
      });
    },
    clearEmployers() {
      this.sourceEmployerId = undefined;
      this.destinationEmployerId = undefined;
      this.employersToDisplay = [];
      this.hideButtons = false;
    },
    prepareMerge() {
      this.employersToDisplay = [
        { ...this.sourceEmployer, role: 'Source' },
        { ...this.destinationEmployer, role: 'Destination' },
      ];
      this.hideButtons = true;
    },
    confirmMergeEmployers() {
      this.$ktoast.confirmToast(`Are you sure you want to merge <i><b>${this.sourceEmployer.name}</b></i> into <i><b>${this.destinationEmployer.name}</b></i>? This action cannot be undone.`, 'warning', () => {
        this.mergeEmployers();
      });
    },
    mergeEmployers() {
      const payload = {
        source_employer_id: this.sourceEmployerId,
        destination_employer_id: this.destinationEmployerId,
      };
      this.$logger.info('Merging employers', undefined, true, payload);
      this.$http.post('/api/profile/employers/merge', payload).then(() => {
        this.$logger.info('Successfully merged employers', undefined, true, payload);
        this.$ktoast.success('Employers merged successfully');
        this.clearEmployers();
        this.getEmployers();
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to merge employers', undefined, err);
        this.$ktoast.showError(err, true);
      });
    },
  },
};
</script>
